import React from 'react'
import { defineMessages, IntlShape } from 'react-intl'

import { Appointment } from '../../models/appointment/Appointment'
import { ConversationObject } from '../../models/conversation/Conversation'
import { type Episode, EpisodeStates } from '../../models/episodes/Episodes'
import { CONVERSATION_STATE, CONVERSATION_TYPE, MessageConversation } from '../../models/messages/Messages'
import { ProgramNames } from '../../models/programs/Programs'
import { fallbackICASPhoneNumber } from '../../models/searchForCare/SearchForCare'
import { CARE_ADVISOR_DISPLAY_PHONE_NUMBER, CARE_NAVIGATOR_EXTENSION } from '../common/constants/constants'
import { isCTPOForProgram } from '../programConfigs/programHelpers'

const childBadgeMessages = defineMessages({
  childBadge: {
    defaultMessage: 'For {name}',
    description: 'Badge label shown above a messaging thread that belongs to a child/dependent of the logged-in parent',
  },
})

export const getChildBadgeLabel = (
  intl: IntlShape,
  appointments: Appointment[],
  patientLyraId: string,
  currentUserId: string,
): string | undefined => {
  if (!currentUserId || patientLyraId === currentUserId) {
    return undefined
  }
  const match = appointments.find((a) => {
    if (a.childLyraId === patientLyraId) {
      return true
    }
    if (a.guardianLyraId && a.userId === patientLyraId) {
      return true
    }
    return false
  })
  if (!match) {
    return undefined
  }
  const firstName = match.childFirstName ?? match.userInfo?.firstName
  if (!firstName) {
    return undefined
  }
  return intl.formatMessage(childBadgeMessages.childBadge, { name: firstName })
}

type SubtitleParameters = {
  isProviderBlocked: boolean
  countryEmergencyNumbers: string[]
  sessionTime: string
  inVideoSession: boolean
  hasActiveAppointment: boolean
  isGenAI: boolean
  isProviderDA?: boolean
  providerName?: string
  contactCarePhoneNumber?: string
  renderLink: (phone: string) => React.ReactNode
  renderContactLyraLink: () => void
}

export const getChatHeaderSubTitleMsg = ({
  isProviderBlocked,
  countryEmergencyNumbers,
  inVideoSession,
  hasActiveAppointment,
  sessionTime,
  isGenAI,
  providerName,
  isProviderDA,
  contactCarePhoneNumber,
  renderLink,
  renderContactLyraLink,
}: SubtitleParameters) => {
  if (isProviderBlocked) {
    if (isProviderDA && contactCarePhoneNumber && countryEmergencyNumbers) {
      return React.createElement(
        React.Fragment,
        null,
        `is unavailable. For immediate crisis support, contact `,
        renderLink(contactCarePhoneNumber),
        `, ext 2, or call `,
        renderLink(countryEmergencyNumbers[0]),
        `.`,
      )
    }
    return `is unavailable. Call ${countryEmergencyNumbers} for emergencies.`
  }
  if (inVideoSession) {
    return ''
  }
  if (isGenAI) {
    return React.createElement(
      React.Fragment,
      null,
      `Lyra AI is not human and can make mistakes. Do not use it for crisis support. For urgent help, `,
      renderContactLyraLink(),
      ` or `,
      renderLink(countryEmergencyNumbers[0]),
      `.`,
    )
  }
  if (hasActiveAppointment) {
    return `Session today from ${sessionTime}`
  }
  if (isProviderDA) {
    if (providerName && contactCarePhoneNumber && countryEmergencyNumbers) {
      // Build a React fragment with clickable tel: links using the provided renderer
      return React.createElement(
        React.Fragment,
        null,
        `${providerName} will respond as soon as they can. For immediate crisis support, contact `,
        renderLink(contactCarePhoneNumber),
        `, ext 2, or call `,
        renderLink(countryEmergencyNumbers[0]),
        `.`,
      )
    }
    // Fallback to plain string if a renderer isn't provided
    return `${
      providerName ?? 'Your provider'
    } will respond as soon as they can. For immediate crisis support, contact ${contactCarePhoneNumber}, ext 2, or call ${countryEmergencyNumbers}.`
  }
  return `Usually responds within 2 days. Call ${countryEmergencyNumbers} for emergencies.`
}

export const isVisibleAiConversation = (conversation: MessageConversation) => {
  return (
    conversation.conversation_attributes?.type === CONVERSATION_TYPE.AI_COACH &&
    (conversation.conversation_state === CONVERSATION_STATE.ACTIVE ||
      conversation.conversation_state === CONVERSATION_STATE.INACTIVE)
  )
}

export const isActiveAiConversation = (conversation: MessageConversation) => {
  return (
    conversation.conversation_attributes?.type === CONVERSATION_TYPE.AI_COACH &&
    conversation.conversation_state === CONVERSATION_STATE.ACTIVE
  )
}

/**
 * showAiCoach: true if the AI Coach feature is enabled and there is at least one active AI Coach conversation.
 * showAiCoachConversation: true if the AI Coach feature is enabled and there is at least one visible AI Coach conversation (active or inactive).
 * aiCoachPromoEnabledProviderIds: an array of provider IDs for which the AI Coach promo should be shown, based on active AI Coach conversations and their associated episodes.
 */
export type AICoachVisibilityState = {
  showAiCoach: boolean
  showAiCoachConversation: boolean
  aiCoachPromoEnabledProviderIds: string[]
}

type EpisodeLinkedConversation = MessageConversation | ConversationObject

export const isActiveGoogleCatalystEpisode = (episode: Episode): boolean =>
  episode.state === EpisodeStates.IN_PROGRESS &&
  isCTPOForProgram(episode.program_taxonomy, ProgramNames.PerformanceCoaching)

const getConversationEpisodeIds = (conversation: EpisodeLinkedConversation): string[] => {
  const episodeIds = conversation.episode_ids?.length
    ? conversation.episode_ids
    : [conversation.conversation_attributes?.episode_id]

  return episodeIds.filter((episodeId): episodeId is string => episodeId !== undefined)
}

const hasAiCoachConversationForActiveGoogleCatalystEpisode = ({
  conversations,
  episodes,
}: {
  conversations: readonly EpisodeLinkedConversation[]
  episodes: readonly Episode[]
}): boolean => {
  const activeGoogleCatalystEpisodeIds = new Set(
    episodes.filter(isActiveGoogleCatalystEpisode).map((episode) => episode.id),
  )

  return conversations.some((conversation) => {
    return (
      conversation.conversation_attributes?.type === CONVERSATION_TYPE.AI_COACH &&
      getConversationEpisodeIds(conversation).some((episodeId) => activeGoogleCatalystEpisodeIds.has(episodeId))
    )
  })
}

export const getGenAiOptOutWithGoogleCatalystOverride = ({
  conversations,
  episodes,
  genAiOptOut,
  isGoogleCatalystLyraAiEnabled,
}: {
  conversations: readonly EpisodeLinkedConversation[]
  episodes: readonly Episode[]
  genAiOptOut: boolean
  isGoogleCatalystLyraAiEnabled: boolean
}): boolean => {
  if (
    genAiOptOut &&
    isGoogleCatalystLyraAiEnabled &&
    hasAiCoachConversationForActiveGoogleCatalystEpisode({
      conversations,
      episodes,
    })
  ) {
    return false
  }

  return genAiOptOut
}

export const getAiCoachPromoState = ({
  conversations,
  isAiCoachEnabled,
  episodes,
}: {
  conversations: MessageConversation[]
  isAiCoachEnabled: boolean
  episodes: Episode[]
}): AICoachVisibilityState => {
  const showAiCoachConversation = isAiCoachEnabled && conversations.some(isVisibleAiConversation)
  const showAiCoach = isAiCoachEnabled && conversations.some(isActiveAiConversation)
  const activeEpisodeIds = conversations.filter(isActiveAiConversation).flatMap(getConversationEpisodeIds)
  const aiCoachPromoEnabledProviderIds = episodes
    .filter((e) => activeEpisodeIds.includes(e.id))
    .map((e) => e.provider_id)

  return {
    showAiCoach,
    showAiCoachConversation,
    aiCoachPromoEnabledProviderIds: showAiCoach ? aiCoachPromoEnabledProviderIds : [],
  }
}

export const getClientHasActiveAiConversation = (
  clientId: string,
  conversations?: MessageConversation[] | ConversationObject[],
) => {
  const clientAiCoachConversations = conversations?.filter(
    (conversation) =>
      conversation.patient_lyra_id === clientId &&
      conversation.conversation_attributes?.type === CONVERSATION_TYPE.AI_COACH,
  )
  const hasActiveAiCoachConversation = clientAiCoachConversations?.some(
    (conversation) => conversation.conversation_state === CONVERSATION_STATE.ACTIVE,
  )
  return Boolean(hasActiveAiCoachConversation)
}

// US toll-free number used by Lyra AI {{toll_free_number}} message wildcard.
export const US_GEN_AI_TOLL_FREE_NUMBER = `${CARE_ADVISOR_DISPLAY_PHONE_NUMBER}, ${CARE_NAVIGATOR_EXTENSION}`

// Default US emergency number used as a fallback for {{emergency_phone_number}}.
export const DEFAULT_EMERGENCY_PHONE_NUMBER = '911'

/**
 * Resolves the toll-free phone number to inject for {{toll_free_number}} in
 * Lyra AI responses. US is a special case — the number isn't stored in
 * country.tollFreeNumbersList; otherwise use the first entry from the list,
 * falling back to fallbackICASPhoneNumber when none is available.
 */
export const resolveGenAiTollFreeNumber = (
  countryIsoCode: string | undefined | null,
  tollFreeNumbersList: string[] | undefined | null,
): string => {
  if (countryIsoCode === 'US') {
    return US_GEN_AI_TOLL_FREE_NUMBER
  }
  return tollFreeNumbersList?.[0] || fallbackICASPhoneNumber
}

/**
 * Resolves the emergency phone number to inject for {{emergency_phone_number}}
 * in Lyra AI responses. Uses the first entry from the country's emergency
 * numbers list, falling back to '911' when the list is missing or empty.
 */
export const resolveGenAiEmergencyNumber = (emergencyPhoneNumbersList: string[] | undefined | null): string => {
  return emergencyPhoneNumbersList?.[0] || DEFAULT_EMERGENCY_PHONE_NUMBER
}
